import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@arco-design/web-react';
import { IconDelete, IconEdit } from '@arco-design/web-react/icon';
import type { TFunction } from 'i18next';
import { get } from 'lodash';

import { MaasModelSourceType } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { getActiveModelSourceType } from '@/delivery-type-plugin/capabilities/maas-init/utils/display';

import gridStyles from './model-sources-grid.module.less';
import type { MaasModelSourceEntry } from './modelInstanceTypes';
import { isExistingPvcModelSource } from './modelSourcePlaceholder';
import { extractSourceRecordFromEntry } from './modelSources';
import { parseStorageQuantityToGi } from './quantity';

function resolveModelSourceTypeLabel(
  sourceRecord: Record<string, unknown>,
  modelName: string | undefined,
  t: TFunction,
): string | undefined {
  if (isExistingPvcModelSource(sourceRecord, modelName)) {
    return t('data-center:maas.model.source.existing.pvc');
  }
  const type = getActiveModelSourceType(sourceRecord);
  if (!type) return undefined;
  return type === MaasModelSourceType.ModelPlaza ? t('home:market.model.repository') : t(`data-center:maas.model.source.${type}`);
}

function resolveModelSourceDetail(sourceRecord: Record<string, unknown>, activeType?: MaasModelSourceType): string | undefined {
  if (!activeType) return undefined;
  const block = sourceRecord[activeType];
  if (!block || typeof block !== 'object') return undefined;
  if (activeType === MaasModelSourceType.StackScope || activeType === MaasModelSourceType.ModelPlaza) {
    const model = String(get(block, 'model') || '').trim();
    const url = String(get(block, 'url') || '').trim();
    return [model, url].filter(Boolean).join(' · ') || undefined;
  }
  const detail = String(get(block, 'url') || get(block, 'model') || get(block, 'secretRef.accessKeyID') || '').trim();
  return detail || undefined;
}

function EntryMetaLine({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <span className={gridStyles.entryMetaLine}>
      <span className={gridStyles.entryMetaLabel}>{label}:</span>
      <span className={gridStyles.entryMetaValue}>{value}</span>
    </span>
  );
}

interface ModelSourceEntryCardProps {
  entry: MaasModelSourceEntry;
  onEdit?: () => void;
  onDelete?: () => void;
  readOnly?: boolean;
}

export function ModelSourceEntryCard({ entry, onEdit, onDelete, readOnly }: ModelSourceEntryCardProps) {
  const { t } = useTranslation();
  const sourceRecord = extractSourceRecordFromEntry(entry as unknown as Record<string, unknown>);
  const activeType = useMemo(() => getActiveModelSourceType(sourceRecord), [sourceRecord]);
  const isExistingPvc = useMemo(() => isExistingPvcModelSource(sourceRecord, entry.name), [entry.name, sourceRecord]);
  const sourceTypeLabel = useMemo(() => resolveModelSourceTypeLabel(sourceRecord, entry.name, t), [entry.name, sourceRecord, t]);
  const sourceDetail = useMemo(
    () => (isExistingPvc ? undefined : resolveModelSourceDetail(sourceRecord, activeType)),
    [activeType, isExistingPvc, sourceRecord],
  );
  const sizeLabel = useMemo(() => {
    const gi = parseStorageQuantityToGi(entry.size);
    if (gi !== undefined && gi > 0) return `${gi}Gi`;
    return entry.size?.trim() || undefined;
  }, [entry.size]);

  const title = entry.name || sourceTypeLabel || t('data-center:maas.section.model');

  return (
    <div className={gridStyles.entryCard}>
      <div className={gridStyles.entryMeta}>
        <div className={gridStyles.entryName}>{title}</div>
        <div className={gridStyles.entrySource}>
          <EntryMetaLine label={t('data-center:maas.model.source')} value={sourceTypeLabel} />
          <EntryMetaLine label={t('data-center:maas.model.size')} value={sizeLabel} />
          <EntryMetaLine label={t('data-center:maas.model.source.detail')} value={sourceDetail} />
        </div>
      </div>
      {!readOnly ? (
        <div className={gridStyles.entryActions}>
          {onEdit ? <Button type="text" size="mini" icon={<IconEdit />} onClick={onEdit} /> : null}
          {onDelete ? <Button type="text" size="mini" status="danger" icon={<IconDelete />} onClick={onDelete} /> : null}
        </div>
      ) : null}
    </div>
  );
}

export function ModelSourcesGrid({
  sources,
  onEditIndex,
  onDeleteIndex,
  readOnly,
}: {
  sources: MaasModelSourceEntry[];
  onEditIndex?: (index: number) => void;
  onDeleteIndex?: (index: number) => void;
  readOnly?: boolean;
}) {
  if (!sources.length) return null;
  return (
    <div className={gridStyles.grid}>
      {sources.map((entry, index) => (
        <ModelSourceEntryCard
          key={`${entry.name}-${index}`}
          entry={entry}
          readOnly={readOnly}
          onEdit={onEditIndex ? () => onEditIndex(index) : undefined}
          onDelete={onDeleteIndex ? () => onDeleteIndex(index) : undefined}
        />
      ))}
    </div>
  );
}
